import React, { useEffect, useRef, useState } from 'react';
import { Button, Text } from '@fragments';
import { IconMicrophone, IconArrowRight, IconHistory, IconArrowLeft, IconX, IconPhoto } from '@tabler/icons-react';
import { errorKey } from '../../../../data/studioApi';
import { hasImage } from '../../../../dialogs/ImageChoiceDialog/format';
import StoryForm from '../../../StoryForm';
import OptionPicker from '../OptionPicker';
import './styles.scss';

// The art's image question (contract 2.9.0, `kind: images`): "Escolher imagens" opens the
// dialog (dialogs/ImageChoiceDialog), recommending the creator's own photos. Every empty
// place needs an image (2.12.0), so the skip, which keeps every current image, is offered
// only when each place already shows one (the template's).
function ImagesQuestion({ studio: s, action }) {
  const { t } = s;
  const [pending, setPending] = useState(null);
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);
  const places = action.images || [];
  const full = places.every(hasImage);
  const choices = [
    { value: 'choose', label: t('imagesChoose'), icon: <IconPhoto />, hint: `${t('imagesPlaces', { count: places.length })} · ${t('imagesChooseHint')}` },
    full && { value: 'skip', label: t('imagesSkipKeep'), icon: <IconArrowRight /> },
  ].filter(Boolean);
  const pick = async choice => {
    if (choice === 'choose') { s.setModal({ type: 'images' }); return; }
    setPending('skip');
    try { await s.skipImages(action); } catch (failure) { s.setError(errorKey(failure)); } finally { if (alive.current) setPending(null); }
  };
  return <>
    <div className="studio-composer-decision__ask"><Text.p id="studio-decision-prompt" contrast={9}>{action.prompt}</Text.p></div>
    <OptionPicker labelledBy="studio-decision-prompt" options={choices} pending={pending} disabled={!!pending} onPick={pick} />
  </>;
}

// The run's pending decision in the composer: the question over its options (the
// same picker as the onboarding), or the inline story form.
export default function ComposerDecision({ studio: s, action, view, setView }) {
  const { t } = s;
  const allowed = action.allowed_responses || [];
  const memories = s.snapshot.memories.filter(memory => (action.candidate_memory_ids || []).includes(memory.id));
  const choices = [
    allowed.includes('submit_story') && { value: 'story', label: t('story'), icon: <IconMicrophone /> },
    allowed.includes('reuse') && memories.length > 0 && { value: 'memories', label: t('chooseMemory'), icon: <IconHistory /> },
    allowed.includes('skip') && { value: 'skip', label: t('skip'), icon: <IconArrowRight /> },
  ].filter(Boolean);
  return <section className={`studio-composer-decision ${view === 'story' ? 'is-story' : ''}`} aria-labelledby="studio-decision-title" tabIndex={-1}>
    <div className="studio-row studio-between studio-composer-decision__heading"><Text.caption id="studio-decision-title" contrast={6}>{t('waiting')}</Text.caption><Button variant="text" color="overlay" size="micro" icon={<IconX />} aria-label={t('cancel')} tooltip={t('cancel')} onClick={s.cancelRun} /></div>
    {action.kind === 'images' ? <ImagesQuestion studio={s} action={action} />
      : view === 'story' ? <StoryForm studio={s} inline onBack={() => setView('choices')} /> : view === 'memories' ? <>
      <div className="studio-composer-decision__ask studio-row studio-between">
        <Text.p id="studio-decision-prompt" contrast={9}>{t('chooseMemory')}</Text.p>
        <Button className="studio-plain-control" size="small" variant="rounded" color="overlay" startIcon={<IconArrowLeft />} preventLoading onClick={() => setView('choices')}>{t('backToChoices')}</Button>
      </div>
      <OptionPicker labelledBy="studio-decision-prompt" options={memories.map(memory => ({ value: memory.id, label: memory.text, icon: <IconHistory /> }))} onPick={id => s.respond('reuse', null, id)} />
    </> : <>
      <div className="studio-composer-decision__ask"><Text.p id="studio-decision-prompt" contrast={9}>{action.prompt}</Text.p></div>
      <OptionPicker labelledBy="studio-decision-prompt" options={choices} onPick={choice => choice === 'skip' ? s.respond('skip') : setView(choice)} />
    </>}
  </section>;
}
